import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, Search, Plus, Users, Hash, X, LogOut, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Chat() {
  const { user } = useAuth();
  const [tab, setTab] = useState("dm"); // dm | rooms
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [active, setActive] = useState(null); // { kind:'dm'|'room', user?, room? }
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [showBrowseRooms, setShowBrowseRooms] = useState(false);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const activeRef = useRef(null);

  useEffect(() => { activeRef.current = active; }, [active]);

  const loadUsers = async () => {
    const [u, c] = await Promise.all([api.get("/users"), api.get("/chat/conversations")]);
    setUsers(u.data); setConversations(c.data);
  };
  const loadRooms = async () => {
    const [m, a] = await Promise.all([api.get("/rooms?mine=true"), api.get("/rooms")]);
    setRooms(m.data); setAllRooms(a.data);
  };

  useEffect(() => {
    loadUsers().catch((e) => toast.error(formatApiError(e)));
    loadRooms().catch(() => {});
  }, []);

  // WebSocket
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("auth_token");
    if (!token) return;
    const wsProtocol = BACKEND_URL.startsWith("https") ? "wss" : "ws";
    const wsBase = BACKEND_URL.replace(/^https?/, wsProtocol);
    const ws = new WebSocket(`${wsBase}/api/ws/chat?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;

    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        const a = activeRef.current;
        if (data.type === "message") {
          const m = data.message;
          if (a?.kind === "dm" && (m.sender_id === a.user.id || m.recipient_id === a.user.id)) {
            setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
          }
          api.get("/chat/conversations").then((r) => setConversations(r.data)).catch(() => {});
        } else if (data.type === "room_message") {
          const m = data.message;
          if (a?.kind === "room" && a.room.id === data.room_id) {
            setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
          }
        }
      } catch {}
    };

    const ping = setInterval(() => {
      if (ws.readyState === 1) ws.send(JSON.stringify({ type: "ping" }));
    }, 25000);

    return () => { clearInterval(ping); ws.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Load messages when active changes
  useEffect(() => {
    if (!active) return;
    (async () => {
      try {
        if (active.kind === "dm") {
          const { data } = await api.get(`/chat/messages/${active.user.id}`);
          setMessages(data);
        } else {
          const { data } = await api.get(`/rooms/${active.room.id}/messages`);
          setMessages(data);
        }
        setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 50);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [active?.kind, active?.user?.id, active?.room?.id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  const sidebarUsers = useMemo(() => {
    const known = new Map();
    conversations.forEach((c) => known.set(c.user.id, { ...c.user, last: c.last_message }));
    users.forEach((u) => { if (!known.has(u.id)) known.set(u.id, u); });
    const arr = Array.from(known.values());
    return search.trim()
      ? arr.filter((u) => u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase()))
      : arr;
  }, [users, conversations, search]);

  const sidebarRooms = useMemo(() => {
    if (!search.trim()) return rooms;
    return rooms.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()));
  }, [rooms, search]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!active || !draft.trim()) return;
    try {
      if (active.kind === "dm") {
        const { data } = await api.post("/chat/messages", { recipient_id: active.user.id, text: draft });
        setMessages((prev) => (prev.some((x) => x.id === data.id) ? prev : [...prev, data]));
      } else {
        const { data } = await api.post(`/rooms/${active.room.id}/messages`, { text: draft });
        setMessages((prev) => (prev.some((x) => x.id === data.id) ? prev : [...prev, data]));
      }
      setDraft("");
    } catch (er) { toast.error(formatApiError(er)); }
  };

  const joinRoom = async (rid) => {
    try {
      await api.post(`/rooms/${rid}/join`);
      await loadRooms();
      toast.success("Joined room");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const leaveRoom = async (rid) => {
    try {
      await api.post(`/rooms/${rid}/leave`);
      await loadRooms();
      if (active?.kind === "room" && active.room.id === rid) setActive(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteRoom = async (rid) => {
    try {
      await api.delete(`/rooms/${rid}`);
      await loadRooms();
      if (active?.kind === "room" && active.room.id === rid) setActive(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="h-[calc(100vh-2rem)] md:h-screen flex">
      {/* Sidebar */}
      <aside className="w-72 border-r border-[#2a2a2a] flex flex-col bg-[#0a0a0a]">
        <div className="p-5 border-b border-[#2a2a2a]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#ff4500] font-mono">── Messages</div>
          <div className="mt-3 flex gap-1">
            <button
              onClick={() => setTab("dm")}
              data-testid="tab-dm"
              className={`flex-1 px-3 py-2 text-[10px] uppercase tracking-[0.2em] border ${tab === "dm" ? "bg-[#1a1a1a] border-[#ff4500] text-[#f5f5f5]" : "border-[#2a2a2a] text-[#a3a3a3] hover:text-[#f5f5f5]"}`}
            >Direct</button>
            <button
              onClick={() => setTab("rooms")}
              data-testid="tab-rooms"
              className={`flex-1 px-3 py-2 text-[10px] uppercase tracking-[0.2em] border ${tab === "rooms" ? "bg-[#1a1a1a] border-[#ff4500] text-[#f5f5f5]" : "border-[#2a2a2a] text-[#a3a3a3] hover:text-[#f5f5f5]"}`}
            >Rooms</button>
          </div>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#a3a3a3]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === "dm" ? "Search people" : "Search rooms"}
              data-testid="chat-search-input"
              className="w-full pl-9 pr-3 py-2 text-sm bg-[#141414] border border-[#2a2a2a] focus:border-[#ff4500] outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {tab === "dm" ? (
            sidebarUsers.length === 0 ? (
              <div className="p-6 text-xs text-[#a3a3a3]">No users to message yet.</div>
            ) : (
              sidebarUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => setActive({ kind: "dm", user: u })}
                  data-testid={`chat-user-${u.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left border-l-2 transition-colors ${
                    active?.kind === "dm" && active.user.id === u.id ? "bg-[#141414] border-[#ff4500]" : "border-transparent hover:bg-[#141414]"
                  }`}
                >
                  <div className="w-10 h-10 bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center overflow-hidden">
                    {u.avatar_url ? (
                      <img src={mediaUrl(u.avatar_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[#ff4500] font-display">{u.name?.[0]?.toUpperCase()}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{u.name}</div>
                    <div className="text-[11px] text-[#a3a3a3] truncate">{u.last?.text || u.email}</div>
                  </div>
                </button>
              ))
            )
          ) : (
            <div>
              <div className="p-3 flex gap-2">
                <button
                  onClick={() => setShowCreateRoom(true)}
                  data-testid="open-create-room"
                  className="flex-1 px-3 py-2 bg-[#ff4500] hover:bg-[#ff5722] text-white text-[10px] uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1"
                >
                  <Plus className="w-3 h-3" /> New
                </button>
                <button
                  onClick={() => setShowBrowseRooms(true)}
                  data-testid="open-browse-rooms"
                  className="flex-1 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] hover:border-[#ff4500] text-[10px] uppercase tracking-[0.2em] inline-flex items-center justify-center gap-1"
                >
                  <Users className="w-3 h-3" /> Browse
                </button>
              </div>
              {sidebarRooms.length === 0 ? (
                <div className="p-6 text-xs text-[#a3a3a3]">You haven't joined any rooms yet.</div>
              ) : (
                sidebarRooms.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setActive({ kind: "room", room: r })}
                    data-testid={`chat-room-${r.id}`}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left border-l-2 transition-colors ${
                      active?.kind === "room" && active.room.id === r.id ? "bg-[#141414] border-[#ff4500]" : "border-transparent hover:bg-[#141414]"
                    }`}
                  >
                    <div className="w-10 h-10 bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center">
                      <Hash className="w-4 h-4 text-[#ff4500]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{r.name}</div>
                      <div className="text-[11px] text-[#a3a3a3] truncate">{r.member_count} member{r.member_count === 1 ? "" : "s"}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Conversation */}
      <section className="flex-1 flex flex-col bg-[#0a0a0a] border-l border-[#2a2a2a]">
        {active ? (
          active.kind === "dm" ? (
            <DMHeader user={active.user} />
          ) : (
            <RoomHeader room={active.room} isOwner={active.room.owner_id === user?.id} onLeave={() => leaveRoom(active.room.id)} onDelete={() => deleteRoom(active.room.id)} />
          )
        ) : null}

        {active ? (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-3 flex flex-col">
              {messages.map((m) => {
                const mine = m.sender_id === user.id;
                return (
                  <div key={m.id} data-testid={`message-${m.id}`} className={`max-w-[75%] ${mine ? "self-end" : "self-start"}`}>
                    {active.kind === "room" && !mine && (
                      <div className="text-[10px] uppercase tracking-[0.2em] text-[#ff4500] font-mono mb-1 ml-1">{m.sender_name}</div>
                    )}
                    <div className={`p-3 text-sm ${
                      mine ? "bg-[#ff4500] text-white" : "bg-[#1a1a1a] text-[#f5f5f5] border border-[#2a2a2a]"
                    }`}>
                      <div className="whitespace-pre-wrap break-words">{m.text}</div>
                      <div className={`text-[10px] mt-1 font-mono uppercase tracking-[0.15em] ${mine ? "text-white/70" : "text-[#a3a3a3]"}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                );
              })}
              {messages.length === 0 && (
                <div className="m-auto text-sm text-[#a3a3a3]">
                  {active.kind === "dm" ? `Say hi to ${active.user.name.split(" ")[0]} ↓` : `Start the conversation in #${active.room.name} ↓`}
                </div>
              )}
            </div>

            <form onSubmit={sendMessage} className="p-4 border-t border-[#2a2a2a] bg-[#0a0a0a] flex gap-2" data-testid="chat-form">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={active.kind === "dm" ? "Type a message…" : `Message #${active.room.name}…`}
                data-testid="chat-message-input"
                className="flex-1 bg-[#141414] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-4 py-2.5 text-sm"
              />
              <button type="submit" data-testid="chat-send-btn"
                className="px-4 py-2.5 bg-[#ff4500] hover:bg-[#ff5722] text-white inline-flex items-center gap-2">
                <Send className="w-4 h-4" /> <span className="text-xs uppercase tracking-[0.2em]">Send</span>
              </button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-[#a3a3a3] flex-col gap-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#ff4500] font-mono">── Select a conversation</div>
            <div>{tab === "dm" ? "Pick someone from the sidebar to start chatting." : "Create or join a room to chat with a group."}</div>
          </div>
        )}
      </section>

      {showCreateRoom && (
        <CreateRoomDialog onClose={() => setShowCreateRoom(false)} onDone={() => { setShowCreateRoom(false); loadRooms(); }} />
      )}
      {showBrowseRooms && (
        <BrowseRoomsDialog
          rooms={allRooms}
          myRoomIds={new Set(rooms.map((r) => r.id))}
          onClose={() => setShowBrowseRooms(false)}
          onJoin={async (rid) => { await joinRoom(rid); }}
        />
      )}
    </div>
  );
}

function DMHeader({ user }) {
  return (
    <header className="px-6 py-4 border-b border-[#2a2a2a] flex items-center gap-3" data-testid="chat-header">
      <div className="w-10 h-10 bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center overflow-hidden">
        {user.avatar_url ? (
          <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-[#ff4500] font-display">{user.name?.[0]?.toUpperCase()}</span>
        )}
      </div>
      <div className="flex-1">
        <div className="font-display text-lg">{user.name}</div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">{user.email}</div>
      </div>
    </header>
  );
}

function RoomHeader({ room, isOwner, onLeave, onDelete }) {
  return (
    <header className="px-6 py-4 border-b border-[#2a2a2a] flex items-center gap-3" data-testid="room-header">
      <div className="w-10 h-10 bg-[#1a1a1a] border border-[#2a2a2a] flex items-center justify-center">
        <Hash className="w-4 h-4 text-[#ff4500]" />
      </div>
      <div className="flex-1">
        <div className="font-display text-lg">{room.name}</div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#a3a3a3]">
          {room.member_count} members · created by {room.owner_name || "?"}
        </div>
      </div>
      {isOwner ? (
        <button onClick={onDelete} data-testid="room-delete-btn" className="text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#ff0033] inline-flex items-center gap-1">
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      ) : (
        <button onClick={onLeave} data-testid="room-leave-btn" className="text-xs uppercase tracking-[0.2em] text-[#a3a3a3] hover:text-[#ff4500] inline-flex items-center gap-1">
          <LogOut className="w-3.5 h-3.5" /> Leave
        </button>
      )}
    </header>
  );
}

function CreateRoomDialog({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr("Room name required"); return; }
    try {
      await api.post("/rooms", { name, description });
      toast.success("Room created");
      onDone();
    } catch (er) { setErr(formatApiError(er)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#141414] border border-[#2a2a2a]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2a2a]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] font-mono">// New room</div>
          <button onClick={onClose} data-testid="close-create-room" className="text-[#a3a3a3] hover:text-[#f5f5f5]"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Room name" data-testid="room-name-input"
            className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm" />
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Description (optional)" data-testid="room-desc-input"
            className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm resize-none" />
          {err && <div className="text-xs text-[#ff0033]">{err}</div>}
          <button type="submit" data-testid="submit-create-room"
            className="w-full px-6 py-3 bg-[#ff4500] hover:bg-[#ff5722] text-white text-sm font-medium">Create</button>
        </form>
      </div>
    </div>
  );
}

function BrowseRoomsDialog({ rooms, myRoomIds, onClose, onJoin }) {
  const [q, setQ] = useState("");
  const list = q.trim() ? rooms.filter((r) => r.name.toLowerCase().includes(q.toLowerCase())) : rooms;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#141414] border border-[#2a2a2a] max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2a2a]">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] font-mono">// Browse rooms</div>
          <button onClick={onClose} data-testid="close-browse-rooms" className="text-[#a3a3a3] hover:text-[#f5f5f5]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 border-b border-[#2a2a2a]">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search rooms" data-testid="browse-search"
            className="w-full bg-[#0a0a0a] border border-[#2a2a2a] focus:border-[#ff4500] outline-none px-3 py-2 text-sm" />
        </div>
        <div className="flex-1 overflow-y-auto">
          {list.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#a3a3a3]">No rooms found.</div>
          ) : (
            list.map((r) => {
              const joined = myRoomIds.has(r.id);
              return (
                <div key={r.id} className="flex items-center justify-between px-5 py-3 border-b border-[#2a2a2a] last:border-b-0" data-testid={`browse-room-${r.id}`}>
                  <div className="min-w-0">
                    <div className="font-display text-sm flex items-center gap-2"><Hash className="w-3.5 h-3.5 text-[#ff4500]" />{r.name}</div>
                    <div className="text-[11px] text-[#a3a3a3] mt-1 truncate">{r.description || `${r.member_count} members · by ${r.owner_name || "?"}`}</div>
                  </div>
                  <button
                    onClick={() => !joined && onJoin(r.id)}
                    disabled={joined}
                    data-testid={`join-room-${r.id}`}
                    className={`px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] ${
                      joined ? "bg-[#1a1a1a] text-[#a3a3a3] border border-[#2a2a2a]" : "bg-[#ff4500] hover:bg-[#ff5722] text-white"
                    }`}
                  >
                    {joined ? "Joined" : "Join"}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
